/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  cmatTimeDailyPreset,
  trendTimeCumulativePreset,
  trendTimeDailyPreset,
  trendTimePercentagePreset,
  trendTimeWeeklyPreset,
} from './tt-presets';
import { ratePlanPreset } from './rate-plan';
import {
  trendHotelCumulativePreset,
  trendHotelDailyPreset,
  trendHotelMovingAveragePreset,
  trendHotelNormalizedPreset,
  trendHotelPercentagePreset,
  trendHotelWeeklyPreset,
} from './th-presets';
import {
  paceSegmentCumulativeLagAlignedPreset,
  paceSegmentCumulativePreset,
  paceSegmentDailyPreset,
  paceSegmentLagAlinedPreset,
} from './ps-presets';
import {
  paceTimeCumulativeLagAlignedPreset,
  paceTimeCumulativePreset,
  paceTimeDailyPreset,
  paceTimeLagAlinedPreset,
} from './pt-presets';
import {
  cmatSegmentDailyPreset,
  trendSegmentCumulativePreset,
  trendSegmentDailyPreset,
  trendSegmentPercentagePreset,
  trendSegmentWeeklyPreset,
} from './ts-presets';
import {
  paceHotelCumulativeLagAlignedPreset,
  paceHotelCumulativePreset,
  paceHotelDailyPreset,
  paceHotelLagAlinedPreset,
} from './ph-presets';
import {
  trendTimeSegmentCumulativePreset,
  trendTimeSegmentDailyPreset,
  trendTimeSegmentWeeklyPreset,
} from './tsx-presets';
import {
  trendHotelSegmentDailyPreset,
  trendHotelSegmentCumulativePreset,
  trendHotelSegmentWeeklyPreset,
} from './thx-presets';
import {
  paceHotelSegmentCumulativeLagAlignedPreset,
  paceHotelSegmentCumulativePreset,
  paceHotelSegmentDailyPreset,
  paceHotelSegmentLagAlinedPreset,
} from './phs-presets';
import { iataDetailsOff, iataDetailsOn } from './iata-presets';
import regionalMap from './regional-map-presets';
import { roomNightsPaceChannel, roomNightsPaceMonthly } from './room-nights';
import { reteCategory, summary, summaryHotel } from './work-book.presets';

// file names are used as key so the right preset can be selected using the filename in runtime
const powerBIVisualPresets = {
  // trend time
  TVT_Daily: trendTimeDailyPreset,
  TVT_Percentage: trendTimePercentagePreset,
  CMATT_Daily: cmatTimeDailyPreset,
  TVT_Cumulative: trendTimeCumulativePreset,
  TVT_Weekly: trendTimeWeeklyPreset,

  // trend segment
  TVS_Daily: trendSegmentDailyPreset,
  TVS_Cumulative: trendSegmentCumulativePreset,
  TVS_Weekly: trendSegmentWeeklyPreset,
  TVS_Percentage: trendSegmentPercentagePreset,
  CMATS_Daily: cmatSegmentDailyPreset,

  // trend hotel
  TVH_Daily: trendHotelDailyPreset,
  TVH_Weekly: trendHotelWeeklyPreset,
  TVH_Cumulative: trendHotelCumulativePreset,
  CMATH_Daily: trendHotelMovingAveragePreset,
  TVH_Normalized: trendHotelNormalizedPreset,
  TVH_Percentage: trendHotelPercentagePreset,

  // trend time segment
  TXS_Daily: trendTimeSegmentDailyPreset,
  TXS_Weekly: trendTimeSegmentWeeklyPreset,
  TXS_Cumulative: trendTimeSegmentCumulativePreset,

  // pace segment
  PVS_Daily: paceSegmentDailyPreset,
  PVS_Cumulative: paceSegmentCumulativePreset,
  PVS_Lag_Aligned: paceSegmentLagAlinedPreset,
  PVS_Cumulative_Lag_Aligned: paceSegmentCumulativeLagAlignedPreset,

  // pace time
  PVT_Cumulative_Lag_Aligned: paceTimeCumulativeLagAlignedPreset,
  PVT_Cumulative: paceTimeCumulativePreset,
  PVT_Lag_Aligned: paceTimeLagAlinedPreset,
  PVT_Daily: paceTimeDailyPreset,

  // pace hotel
  PVH_Cumulative_Lag_Aligned: paceHotelCumulativeLagAlignedPreset,
  PVH_Cumulative: paceHotelCumulativePreset,
  PVH_Lag_Aligned: paceHotelLagAlinedPreset,
  PVH_Daily: paceHotelDailyPreset,

  // pace segment hotel
  PHS_Cumulative_Lag_Aligned: paceHotelSegmentCumulativeLagAlignedPreset,
  PHS_Cumulative: paceHotelSegmentCumulativePreset,
  PHS_Lag_Aligned: paceHotelSegmentLagAlinedPreset,
  PHS_Daily: paceHotelSegmentDailyPreset,

  // pace time segment
  PTS_Cumulative_Lag_Aligned: paceTimeCumulativeLagAlignedPreset,
  PTS_Cumulative: paceTimeCumulativePreset,
  PTS_Lag_Aligned: paceTimeLagAlinedPreset,
  PTS_Daily: paceTimeDailyPreset,

  // Rate Plan
  Rate_Plan: ratePlanPreset,

  // trend time segment
  TXH_Daily: trendHotelSegmentDailyPreset,
  TXH_Weekly: trendHotelSegmentWeeklyPreset,
  TXH_Cumulative: trendHotelSegmentCumulativePreset,

  // Iata
  IATA_Details_On: iataDetailsOn,
  IATA_Details_Off: iataDetailsOff,

  // RegionalMap
  RegionalMap: regionalMap,
  //
  Room_Nights_Pace_Channel_Monthly: roomNightsPaceMonthly,
  //
  Summary_1f564914fc738bbfaf58: summary,
  Summary_edfb7ec20176cf935522: summary,
  Summary_237f73d65ba78ea8322d: summary,
  Summary_a42e9a818c8190778011: summary,
  //
  Summary_Hotel_1a740f94120920431e68: summaryHotel,
  Summary_Hotel_1326c34fc61c56345da2: summaryHotel,
  Summary_Hotel_8e9d1a68902008cb2c5c: summaryHotel,
  Summary_Hotel_20622362a93d0cdc7d6e: summaryHotel,
  //
  Rate_Category_667fcf26066bb41d9bb1: reteCategory,
  Rate_Category_7efe7187929479673ece: reteCategory,
  Rate_Category_d6d19dd9d944de8c5adf: reteCategory,
  Rate_Category_ef292b9fbb973a466691: reteCategory,
  Rate_Category_8f5a3bbb333ac1dd0194: reteCategory,
  Rate_Category_b5bb4d2cf834d97584d8: reteCategory,
  //
  Rate_Category_Hotel_3d400e5a3aa9ad0e2076: reteCategory,
  Rate_Category_Hotel_bc25889300e6ec272e3a: reteCategory,
  Rate_Category_Hotel_e51f412be406713ea5a8: reteCategory,
  Rate_Category_Hotel_ec88a2a57dd626663231: reteCategory,
  Rate_Category_Hotel_3f8ec5536984d182b280: reteCategory,
  Rate_Category_Hotel_92c80e6780c4c32c90e0: reteCategory,
  //
  Room_Night_Pace_Channel: roomNightsPaceChannel,
};

export default powerBIVisualPresets;
